<template>
  <div class="seat-config">
    <div class="step-badge">
      <i class="el-icon-s-order"></i>
      <span>{{ $t('buy.steps.badge.step4') }}</span>
    </div>
    <div class="section-header">
      <h3>{{ $t('buy.licences.title') }}</h3>
      <p class="section-desc">{{ $t('buy.licences.subtitle') }}</p>
    </div>

    <div class="seats-grid">
      <!-- 审计员席位卡片 -->
      <div 
        class="seat-card auditor-card" 
        :class="{ 'is-locked': planId === 'starter' }"
        @click="checkStarterPlan"
      >
        <div class="card-header">
          <div class="card-icon auditor-icon">
            <i class="el-icon-user-solid"></i>
          </div>
          <div class="card-title-group">
            <h4>{{ $t('buy.licences.auditor.title') }}</h4>
            <p>{{ $t('buy.licences.auditor.desc') }}</p>
          </div>
        </div>

        <div class="interactive-area" :class="{ 'pointer-none': planId === 'starter' }">
          <div class="seat-counter">
            <el-button 
              circle 
              icon="el-icon-minus" 
              size="small"
              :disabled="currentSeatIndex <= 0 || planId === 'starter'"
              @click="currentSeatIndex--"
              class="counter-btn"
            />
            <div class="seat-number">
              <span class="count">{{ auditorSeats }}</span>
              <span class="label">{{ $t('buy.licences.auditor.seats') }}</span>
            </div>
            <el-button 
              circle 
              icon="el-icon-plus" 
              size="small"
              :disabled="currentSeatIndex >= auditorOptions.length - 1 || planId === 'starter'"
              @click="currentSeatIndex++"
              class="counter-btn"
            />
          </div>

          <el-slider 
            v-model="currentSeatIndex" 
            :min="0" 
            :max="auditorOptions.length - 1"
            :step="1"
            show-stops
            :disabled="planId === 'starter'"
            :format-tooltip="formatSliderTooltip"
            class="seat-slider"
          />

          <div class="seat-options">
            <div 
              v-for="option in auditorOptions" 
              :key="option.value"
              class="option-item"
              :class="{ 
                active: auditorSeats === option.value,
                disabled: planId === 'starter' && auditorSeats !== option.value
              }"
              @click="planId === 'starter' ? null : auditorSeats = option.value"
            >
              <div class="option-header">
                <span class="option-value">{{ option.value }}</span>
                <span class="option-label">{{ $t('buy.licences.auditor.seats') }}</span>
              </div>
              <div class="option-footer">
                <span class="option-price">{{ currency }}  {{ formatPrice(option.pricePerSeat) }}</span>
              </div>
            </div>
            <div class="option-item contact-more" @click="planId === 'starter' ? null : handleContactService">
              <div class="option-header">
                <span class="option-value">> 50</span>
                <span class="option-label">{{ $t('buy.licences.auditor.seats') }}</span>
              </div>
              <div class="option-footer">
                <span class="option-price">{{ $t('buy.licences.contactMore') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="seat-price-display">
          <div class="price-row">
            <span>{{ auditorSeats }} {{ $t('buy.licences.auditor.seats') }} × {{ currency }}  {{ formatPrice(currentAuditorPrice) }}/{{ $t('buy.licences.perMonth') }}</span>
            <span class="price-value">{{ currency }}  {{ formatPrice(auditorTotalPrice) }}/{{ $t('buy.licences.perYear') }}</span>
          </div>
        </div>
      </div>

      <!-- 管理员席位卡片 -->
      <div class="seat-card admin-card" v-if="planId !== 'starter'">
        <div class="card-header">
          <div class="card-icon admin-icon">
            <i class="el-icon-s-custom"></i>
          </div>
          <div class="card-title-group">
            <h4>{{ $t('buy.licences.admin.title') }}</h4>
            <p>{{ $t('buy.licences.admin.desc') }}</p>
          </div>
        </div>

        <div class="admin-info">

          <div class="admin-extra" v-if="extraAdminSeats > 0">
            <span>+ {{ extraAdminSeats }} {{ $t('buy.licences.admin.extraSeats') }}</span>
            <span class="extra-price">{{ currency }}  {{ formatPrice(extraAdminTotalPrice) }}/{{ $t('buy.licences.perYear') }}</span>
          </div>
        </div>

        <div class="admin-counter">
          <span class="counter-label">{{ $t('buy.licences.admin.extraLabel') }}</span>
          <el-input-number 
            v-model="extraAdminSeats" 
            :min="0" 
            :max="10"
            size="small"
            class="admin-input-number"
          />
          <span class="extra-unit-price">× {{ currency }}  {{ formatPrice(adminExtraPrice) }}/{{ $t('buy.licences.perMonth') }}</span>
        </div>
      </div>

      <div class="seat-card supplier-card">
        <div class="card-header">
          <div class="card-icon supplier-icon">
            <i class="el-icon-office-building"></i>
          </div>
          <div class="card-title-group">
            <h4>{{ $t('buy.licences.supplier.title') }}</h4>
            <p>{{ $t('buy.licences.supplier.desc') }}</p>
          </div>
        </div>

        <div class="supplier-info">
          <div class="unlimited-badge">
            <i class="el-icon-check"></i>
            <span>{{ $t('buy.licences.supplier.unlimited') }}</span>
          </div>
          <p class="supplier-note">{{ $t('buy.licences.supplier.note') }}</p>
        </div>

        <div class="supplier-features">
          <div class="feature-item" v-for="(f, idx) in 4" :key="idx">
            <i class="el-icon-circle-check"></i>
            <span>{{ $t(`buy.licences.supplier.features.${idx}`) }}</span>
          </div>
        </div>
      </div>

      <!-- 客户门户 -->
      <div class="seat-card client-card">
        <div class="card-header">
          <div class="card-icon client-icon">
            <i class="el-icon-monitor"></i>
          </div>
          <div class="card-title-group">
            <h4>{{ $t('buy.licences.client.title') }}</h4>
            <p>{{ $t('buy.licences.client.desc') }}</p>
          </div>
        </div>

        <div class="supplier-info">
          <div class="unlimited-badge">
            <i class="el-icon-check"></i>
            <span>{{ $t('buy.licences.client.unlimited') }}</span>
          </div>
          <p class="supplier-note">{{ $t('buy.licences.client.note') }}</p>
        </div>

        <div class="supplier-features">
          <div class="feature-item" v-for="(f, idx) in 4" :key="idx">
            <i class="el-icon-circle-check"></i>
            <span>{{ $t(`buy.licences.client.features.${idx}`) }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- 价格汇总 -->
    <div class="seats-total">
      
      <div class="total-row">
        <span class="total-label">{{ $t('buy.licences.totalSeats') }}:</span>
        <span class="total-value">{{ auditorSeats + includedAdminSeats + extraAdminSeats }} {{ $t('buy.licences.users') }}</span>
      </div>
      <div class="total-row highlight">
        <span class="total-label">{{ $t('buy.licences.seatsCost') }}:</span>
        <span class="total-price">{{ currency }}  {{ formatPrice(seatsTotalPrice) }}/{{ $t('buy.licences.perYear') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { formatPrice as formatPriceValue } from '@/utils/currency'
export default {
  name: 'SeatConfig',
  props: {
    planId: {
      type: String,
      default: 'professional'
    },
    purchaseConfig: {
      type: Object,
      default: null
    },
    currency: {
      type: String,
      default: 'RMB'
    },
    value: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      auditorSeats: 1,
      extraAdminSeats: 0,
      includedAdminSeats: 0,
      adminExtraPrice: 60,
      auditorOptions: [
        { value: 1, pricePerSeat: 60},
        { value: 3, pricePerSeat: 58 },
        { value: 5, pricePerSeat: 56 },
        { value: 15, pricePerSeat: 53 },
        { value: 25, pricePerSeat: 51 },
        { value: 50, pricePerSeat: 49 }
      ]
    }
  },
  computed: {
    currentAuditorPrice() {
      const option = this.auditorOptions.find(o => o.value === this.auditorSeats)
      return option ? option.pricePerSeat : 0
    },
    auditorTotalPrice() {
      return this.auditorSeats * this.currentAuditorPrice * 12
    },
    extraAdminTotalPrice() {
      return this.extraAdminSeats * this.adminExtraPrice * 12
    },
    seatsTotalPrice() {
      return this.auditorTotalPrice + this.extraAdminTotalPrice
    },
    currentSeatIndex: {
      get() {
        return this.auditorOptions.findIndex(o => o.value === this.auditorSeats)
      },
      set(val) {
        if (this.auditorOptions[val]) {
          this.auditorSeats = this.auditorOptions[val].value
        }
      }
    }
  },
  watch: {
    purchaseConfig: {
      handler() {
        this.applyPurchaseConfig()
      },
      deep: true
    },
    seatsTotalPrice() {
      this.emitConfig()
    },
    planId: {
      handler(val) {
        if (val === 'starter') {
          this.applyPlanSeatLimits()
        }
      },
      immediate: true
    },
    value: {
      handler() {
        this.applyValue()
      },
      deep: true
    }
  },
  created() {
    this.applyValue()
    this.applyPurchaseConfig()
    this.$emit('validate', true)
  },
  methods: {
    emitConfig() {
      this.$emit('seats-configured', {
        auditorSeats: this.auditorSeats,
        extraAdminSeats: this.extraAdminSeats,
        totalPrice: this.seatsTotalPrice
      })
    },
    applyValue() {
      if (!this.value) return
      if (typeof this.value.auditorSeats === 'number') {
        this.auditorSeats = this.value.auditorSeats
      }
      if (typeof this.value.extraAdminSeats === 'number') {
        this.extraAdminSeats = this.value.extraAdminSeats
      }
      this.applyPlanSeatLimits()
    },
    applyPlanSeatLimits() {
      if (this.planId === 'starter') {
        this.extraAdminSeats = 0
        this.auditorSeats = 1
      }
    },
    applyPurchaseConfig() {
      if (!this.purchaseConfig) return
      if (Array.isArray(this.purchaseConfig.auditorSeatPrices)) {
        this.auditorOptions = this.purchaseConfig.auditorSeatPrices.map(item => ({
          value: item.seats,
          pricePerSeat: this.fromMinor(item.pricePerSeat)
        }))
      }
      if (this.purchaseConfig.adminSeatPrice != null) {
        this.adminExtraPrice = this.fromMinor(this.purchaseConfig.adminSeatPrice)
      }
      this.emitConfig()
    },
    fromMinor(amount) {
      return Math.round(Number(amount || 0)) / 100
    },
    checkStarterPlan() {
      if (this.planId === 'starter') {
        this.$message({
          message: this.$t('buy.licences.planChangePrompt'),
          type: 'warning'
        });
      }
    },
    handleContactService() {
      // Logic to implement when contact service is clicked
      window.open('mailto:support@lpt.com', '_blank')
    },
    formatPrice(price) {
      if (typeof price !== 'number') return formatPriceValue(0)
      return formatPriceValue(price)
    },
    formatSliderTooltip(val) {
      return this.auditorOptions[val] ? this.auditorOptions[val].value : ''
    }
  }
}
</script>

<style lang="scss" scoped>
.seat-config {
  max-width: 900px;
  margin: 0 auto;
  position: relative;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 24px;
  i {
    font-size: 14px;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 36px;

  h3 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
  }

  .section-desc {
    color: #64748b;
    font-size: 15px;
    margin: 0;
  }
}

.seats-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.seat-card {
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 28px;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  }

  &.is-locked {
    cursor: pointer;
    background: #f8fafc;
    border-style: dashed;
    
    .interactive-area {
      opacity: 0.6;
      filter: grayscale(0.5);
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    .card-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      i {
        font-size: 24px;
        color: white;
      }

      &.auditor-icon {
        background: linear-gradient(135deg, #6366f1, #818cf8);
      }
      &.admin-icon {
        background: linear-gradient(135deg, #2d8cf0, #5cadff);
      }
      &.supplier-icon {
        background: linear-gradient(135deg, #e6a700, #ffc107);
      }
      &.client-icon {
        background: linear-gradient(135deg, #059669, #10b981);
      }
    }

    .card-title-group {
      h4 {
        font-size: 18px;
        font-weight: 700;
        color: #1e293b;
        margin: 0 0 4px;
      }
      p {
        font-size: 14px;
        color: #64748b;
        margin: 0;
      }
    }
  }
}

.seat-options {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  .option-item {
    padding: 0;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s;
    text-align: center;
    min-width: 100px;
    background: #fff;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    &:hover {
      border-color: #cbd5e1;
      transform: translateY(-2px);
    }

    &.active {
      border-color: #6366f1;
      background: #eef2ff;
      box-shadow: 0 4px 12px rgba(99,102,241,0.15);

      .option-header .option-value {
        color: #4f46e5;
      }
      
      .option-footer {
        background: #4f46e5;
        .option-price {
          color: #fff;
        }
      }
    }

    &.contact-more {
      .option-value {
        font-size: 20px;
        color: #6366f1;
      }
      .option-footer .option-price {
        font-size: 12px;
      }
    }

    .option-header {
      padding: 12px 10px;
      
      .option-value {
        display: block;
        font-size: 24px;
        font-weight: 800;
        color: #334155;
        line-height: 1.2;
      }

      .option-label {
        display: block;
        font-size: 12px;
        color: #94a3b8;
        margin-top: 2px;
      }
    }

    .option-footer {
      background: #f1f5f9;
      padding: 6px 4px;
      transition: all 0.3s;

      .option-price {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: #64748b;
      }
    }
  }
}

.seat-counter {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  margin-bottom: 24px;

  .counter-btn {
    border: 2px solid #e2e8f0 !important;
    transition: all 0.2s;
    
    &:hover:not(:disabled) {
      border-color: #6366f1 !important;
      color: #6366f1 !important;
    }
  }

  .seat-number {
    text-align: center;
    
    .count {
      display: block;
      font-size: 48px;
      font-weight: 800;
      color: #6366f1;
      line-height: 1;
    }
    .label {
      font-size: 13px;
      color: #94a3b8;
      margin-top: 4px;
      display: block;
    }
  }
}

.seat-slider {
  margin-bottom: 32px;
  padding: 0 10px;

  ::v-deep .el-slider__runway {
    height: 8px;
    border-radius: 4px;
  }
  
  ::v-deep .el-slider__bar {
    background: linear-gradient(90deg, #6366f1, #818cf8);
    height: 8px;
    border-radius: 4px;
  }

  ::v-deep .el-slider__button {
    width: 22px;
    height: 22px;
    border: 3px solid #6366f1;
    box-shadow: 0 2px 8px rgba(99,102,241,0.3);
  }
}

.seat-price-display {
  background: #f8fafc;
  border-radius: 10px;
  padding: 14px 18px;

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #475569;

    .price-value {
      font-weight: 700;
      font-size: 18px;
      color: #6366f1;
    }
  }
}

.admin-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;

  .admin-included {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #2d8cf0;
    font-weight: 600;
    font-size: 15px;

    i {
      font-size: 20px;
    }
  }

  .admin-extra {
    display: flex;
    justify-content: space-between;
    padding: 10px 14px;
    background: #f0f7ff;
    border-radius: 8px;
    font-size: 14px;
    color: #2d8cf0;

    .extra-price {
      font-weight: 600;
    }
  }
}

.admin-counter {
  display: flex;
  align-items: center;
  gap: 12px;

  .counter-label {
    font-size: 14px;
    color: #475569;
    font-weight: 500;
  }

  .admin-input-number {
    width: 140px;
  }

  .extra-unit-price {
    font-size: 13px;
    color: #94a3b8;
  }
}

.supplier-info {
  margin-bottom: 20px;

  .unlimited-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;

    i {
      font-size: 22px;
      padding: 4px;
      border-radius: 50%;
    }
  }

  .pointer-none {
    pointer-events: none;
  }

  .supplier-note {
    font-size: 14px;
    color: #64748b;
    margin: 0;
  }
}

/* Supplier Card Theme Overrides */
.supplier-card {
  .unlimited-badge {
    color: #e6a700;
    i { background: rgba(230, 167, 0, 0.15); }
  }
  .feature-item {
    background: rgba(230, 167, 0, 0.05);
    i { color: #e6a700; }
  }
}

/* Client Card Theme Overrides */
.client-card {
  .unlimited-badge {
    color: #10b981;
    i { background: #d1fae5; }
  }
  .feature-item {
    background: #f0fdf4;
    i { color: #10b981; }
  }
}

.supplier-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  .feature-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #475569;
    padding: 8px 12px;
    border-radius: 8px;

    i {
      font-size: 16px;
    }
  }
}

.seats-total {
  margin-top: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f0f4ff, #ede9fe);
  border-radius: 14px;

  .total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;

    .total-label {
      font-size: 15px;
      color: #475569;
      font-weight: 500;
    }

    .total-value {
      font-size: 15px;
      color: #1e293b;
      font-weight: 600;
    }

    &.highlight {
      border-top: 1px solid #c7d2fe;
      padding-top: 14px;
      margin-top: 6px;

      .total-price {
        font-size: 24px;
        font-weight: 800;
        color: #4f46e5;
      }
    }
  }
}

@media (max-width: 768px) {
  .step-badge {
    padding: 5px 12px;
    margin-bottom: 16px;
    font-size: 12px;
  }

  .section-header {
    text-align: left;
    margin-bottom: 24px;

    h3 {
      font-size: 21px;
    }

    .section-desc {
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .seat-card {
    padding: 18px;
    border-width: 1px;
    border-radius: 12px;

    .card-header {
      align-items: flex-start;
      margin-bottom: 20px;
    }
  }

  .seat-counter {
    gap: 22px;
  }

  .seat-slider {
    margin-bottom: 20px;
  }

  .seat-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;

    .option-item {
      min-width: 0;
    }
  }

  .seat-price-display .price-row,
  .admin-info .admin-extra,
  .seats-total .total-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .admin-counter {
    align-items: flex-start;
    flex-direction: column;

    .admin-input-number {
      width: 100%;
    }
  }

  .supplier-features {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .seat-card {
    padding: 14px;

    .card-header {
      gap: 12px;

      .card-icon {
        width: 42px;
        height: 42px;
      }

      .card-title-group h4 {
        font-size: 16px;
      }
    }
  }

  .seat-counter .seat-number .count {
    font-size: 36px;
  }

  .seat-options {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-bottom: 20px;

    .option-item {
      .option-header .option-value {
        font-size: 20px;
      }

      .option-footer .option-price {
        font-size: 12px;
      }
    }
  }

  .supplier-info .unlimited-badge {
    font-size: 16px;
  }

  .seats-total {
    padding: 14px;
    border-radius: 12px;

    .total-row.highlight .total-price {
      font-size: 20px;
    }
  }
}
</style>
